<template>
  <div class="monitoring-container">
    <div class="monitoring-rectangle">
      
      <!-- Coin Supérieur Gauche - BPM -->
      <div v-show="showMonitoring" class="corner top-left">
        <BpmBubble :bpm="bpmData.bpm" />
        <span class="corner-label">BPM</span>
      </div>

      <!-- Coin Supérieur Droit - Moyenne Cohérence -->
      <div v-show="showMonitoring" class="corner top-right">
        <div class="average-display">
          <div class="value">{{ averageCC || '--' }}</div>
          <div class="unit">%</div>
        </div>
        <span class="corner-label">MOY. CC</span>
      </div>

      <!-- Coin Inférieur Gauche - Jauge Cohérence -->
      <div v-show="showMonitoring" class="corner bottom-left">
        <CoherenceCardiaqueGauge :coherenceValue="bpmData.cc" />
        <span class="corner-label">COHÉRENCE</span>
      </div>

      <!-- Coin Inférieur Droit - Guide Respiratoire -->
      <div v-show="showMonitoring" class="corner bottom-right">
        <BreathingGuide />
        <span class="corner-label">RESPIRATION</span>
      </div>

      <!-- Bouton pour cacher les éléments -->
      <button class="toggle-button" @click="toggleMonitoring" :class="{ active: showMonitoring }">
        <span v-if="showMonitoring">👁️</span>
        <span v-else>👁️‍🗨️</span>
      </button>

      <!-- Zone centrale -->
      <div class="center-zone">
        <h2>Zone de Jeu</h2>
        <div class="session-info">
          <span class="session-time">{{ formatTime(sessionTime) }}</span>
          <div class="status-indicator" :class="connectionStatusClass"></div>
        </div>
      </div>
      
    </div>
  </div>
</template>

<script>
import { connectSymbioCenter } from "@/services/symbiosensor/SocketService";
import BpmBubble from "@/components/monitorage/moniteurExpert/BpmBubble.vue";
import BreathingGuide from "@/components/monitorage/moniteurExpert/GuideRespiratoire.vue";
import CoherenceCardiaqueGauge from "@/components/monitorage/moniteurExpert/CoherenceCardiaqueGauge.vue";

export default {
  name: 'MonitorageJeux',
  components: {
    BpmBubble,
    BreathingGuide,
    CoherenceCardiaqueGauge
  },
  data() {
    return {
      symbioService: null,
      isConnected: false,
      hasSensorData: false,
      sessionTime: 0,
      sessionTimer: null,
      showMonitoring: true, // Contrôle la visibilité des coins
      bpmData: {
        bpm: 0,
        cc: 0,
        rr: 0,
        pctT1: 0,
        pctT2: 0,
        pctT3: 0,
        traceTacho: []
      },
      ccHistory: []
    }
  },
  computed: {
    connectionStatusClass() {
      if (!this.isConnected) return 'disconnected';
      if (this.hasSensorData && this.bpmData.bpm > 0 && this.bpmData.bpm < 40) return 'error';
      if (this.hasSensorData) return 'connected';
      return 'waiting';
    },
    
    averageCC() {
      if (this.ccHistory.length === 0) return null;
      const sum = this.ccHistory.reduce((acc, val) => acc + val, 0);
      return Math.round(sum / this.ccHistory.length);
    }
  },
  created() {
    this.symbioService = connectSymbioCenter({
      onBpmUpdate: this.handleBpmUpdate,
      onConnectionChange: this.handleConnectionChange,
      os: navigator.platform.indexOf("Mac") !== -1 ? "Mac" : "Windows",
    });

    this.symbioService.connect();
    this.startTimer();
  },
  beforeUnmount() {
    if (this.symbioService) {
      this.symbioService.disconnect();
    }
    if (this.sessionTimer) {
      clearInterval(this.sessionTimer);
    }
  },
  methods: {
    handleBpmUpdate(data) {
      this.bpmData = { ...data };
      
      if (data.cc && data.cc > 0) {
        this.ccHistory.push(data.cc);
        if (this.ccHistory.length > 50) {
          this.ccHistory.shift();
        }
      }
    },
    
    handleConnectionChange(status) {
      this.isConnected = status.isSocketConnected;
      this.hasSensorData = status.hasSensorData;
    },
    
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    
    startTimer() {
      this.sessionTimer = setInterval(() => {
        this.sessionTime++;
      }, 1000);
    },

    toggleMonitoring() {
      this.showMonitoring = !this.showMonitoring;
    }
  }
}
</script>

<style scoped>
.monitoring-container {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f0f23, #1a1a2e);
  padding: 20px;
}

.monitoring-rectangle {
  width: 95%;
  height: 85%;
  max-width: 1200px;
  max-height: 700px;
  background: rgba(30, 30, 50, 0.9);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(20px);
}

/* === COINS === */
.corner {
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 100px;
  min-height: 100px;
  /* Les coins s'adaptent à leur contenu */
  width: fit-content;
  height: fit-content;
}

.corner-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 700;
  margin-top: 8px;
}

/* Positions des coins */
.top-left {
  top: 20px;
  left: 20px;
}

.top-right {
  top: 20px;
  right: 20px;
}

.bottom-left {
  bottom: 20px;
  left: 20px;
}

.bottom-right {
  bottom: 20px;
  right: 20px;
}

/* === BOUTON TOGGLE === */
.toggle-button {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 16px;
  z-index: 10;
}

.toggle-button:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.toggle-button.active {
  background: rgba(76, 175, 80, 0.2);
  border-color: rgba(76, 175, 80, 0.4);
}

/* === TRANSITION DES COINS === */
.corner {
  transition: all 0.3s ease;
}

/* === AFFICHAGE MOYENNE CC === */
.average-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.value {
  color: #fff;
  font-size: 36px;
  font-weight: 900;
  line-height: 1;
}

.unit {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 600;
}

/* === ZONE CENTRALE === */
.center-zone {
  text-align: center;
  color: #fff;
  z-index: 1;
}

.center-zone h2 {
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 20px 0;
  font-size: 28px;
  font-weight: 300;
}

.session-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.session-time {
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  font-family: monospace;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.status-indicator.connected {
  background: #4caf50;
  box-shadow: 0 0 8px #4caf50;
}

.status-indicator.waiting {
  background: #ffc107;
  box-shadow: 0 0 8px #ffc107;
}

.status-indicator.disconnected {
  background: #f44336;
  box-shadow: 0 0 8px #f44336;
}

.status-indicator.error {
  background: #ff5722;
  animation: blink 1s infinite;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0.3; }
}

/* === RESPONSIVE === */
@media (max-width: 768px) {
  .monitoring-rectangle {
    width: 85%;
    height: 75%;
  }
  
  .corner {
    min-width: 80px;
    min-height: 80px;
    padding: 10px;
  }
  
  .corner-label {
    font-size: 7px;
  }
  
  .value {
    font-size: 24px;
  }
  
  .center-zone h2 {
    font-size: 22px;
  }
  
  .session-time {
    font-size: 16px;
  }
  
  .toggle-button {
    width: 35px;
    height: 35px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .corner {
    min-width: 70px;
    min-height: 70px;
    padding: 8px;
    border-radius: 12px;
  }
  
  .top-left, .top-right {
    top: 15px;
  }
  
  .bottom-left, .bottom-right {
    bottom: 15px;
  }
  
  .top-left, .bottom-left {
    left: 15px;
  }
  
  .top-right, .bottom-right {
    right: 15px;
  }
  
  .corner-label {
    font-size: 6px;
  }
  
  .value {
    font-size: 20px;
  }
  
  .center-zone h2 {
    font-size: 18px;
  }
  
  .toggle-button {
    width: 30px;
    height: 30px;
    font-size: 12px;
    top: 8px;
    right: 8px;
  }
}

/* === ADAPTATION AUX COMPOSANTS === */
/* Les composants s'adaptent automatiquement grâce aux coins en fit-content */
.corner :deep(*) {
  max-width: 100%;
  max-height: 100%;
}
</style>